import { get, groupBy } from 'lodash';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import i18next from 'i18next';
import React from 'react';

import { convertUnits, getFactorToConvertMetricToEntityUnit } from '@commons/utils/conversion';
import DisplayNumber from '@commons/DisplayNumber';
import PictureWithActions from '@commons/PictureWithActions';

import {
  getIconActions,
  removeItemCell,
  renderBadgeCell,
  renderDropdownCell,
  renderInputCell,
  renderNameCell,
} from '../utils/customCells';

import Text from '@commons/Text';

export const getRecipesColumns = ({
  lossCategories,
  currency,
  handleFieldUpdate,
  handleItemRemoval,
  recipeRows,
  hasMultipleChannels,
  isCentralMode,
  canCreateOrUpdateLosses,
  canCorrectLosses,
  handleReplacePicture,
  handleDeletePicture,
  setShouldPictureFullScreen,
}) => {
  const canEdit = canCreateOrUpdateLosses && canCorrectLosses;

  const onReplacePicture = (lossId) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (event) => handleReplacePicture(event, lossId);
    fileInput.click();
  };

  const columns = [
    {
      field: 'badge',
      headerName: '',
      width: 1,
      minWidth: 1,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.isCashierLoss) {
          return renderBadgeCell();
        }

        return null;
      },
    },
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      width: 40,
    },
    {
      field: 'name',
      headerName: i18next.t('GENERAL.NAME'),
      width: 400,
      sortable: true,
      renderCell: ({ row }) => renderNameCell(row),
    },
    {
      field: 'quantity',
      headerName: i18next.t('GENERAL.QUANTITY'),
      width: 180,
      sortable: true,
      renderCell: ({ row }) =>
        renderInputCell(
          row,
          (value) => handleFieldUpdate('quantity', value, row.id),
          row.isCashierLoss || !canEdit,
        ),
    },
    {
      field: 'unit',
      headerName: i18next.t('GENERAL.UNIT'),
      width: 70,
      sortable: false,
      renderCell: ({ row }) => <Text>{convertUnits(row.unit)}</Text>,
    },
    {
      field: 'category',
      headerName: i18next.t('GENERAL.CATEGORY'),
      width: 180,
      sortable: false,
      renderCell: ({ row }) => {
        const selectedCategory = lossCategories.find(({ id }) => id === row.lossCategoryId);

        const recipeRowGroupedByRids = groupBy(recipeRows, 'recipeId');
        const associatedLosses = recipeRowGroupedByRids[row.recipeId] || [];

        // If !hasMultipleChannels we have a unicity check on the categories only
        if (!hasMultipleChannels) {
          const associatedLossesCategoryIds = new Set(
            associatedLosses.map(({ lossCategoryId }) => lossCategoryId),
          );

          const formattedLossCategories = lossCategories.map((lossCategory) => ({
            ...lossCategory,
            isDisabled: associatedLossesCategoryIds.has(lossCategory.id),
          }));

          return renderDropdownCell(
            formattedLossCategories,
            selectedCategory,
            (item) => handleFieldUpdate('lossCategoryId', item.id, row.id),
            row.isCashierLoss || !canEdit,
          );
        }

        /*
          If hasMultipleChannels we have a unicity check on the categories and channels meaning
          we can have the same lossCategory for one recipe as long as the channel is different.
        */
        const allAssociatedLossesCompositeKeys = new Set(associatedLosses.map(({ key }) => key));

        const formattedLossCategories = lossCategories.map((lossCategory) => ({
          ...lossCategory,
          isDisabled: allAssociatedLossesCompositeKeys.has(`${row.channelId}-${lossCategory.id}`),
        }));

        return renderDropdownCell(
          formattedLossCategories,
          selectedCategory,
          (item) => handleFieldUpdate('lossCategoryId', item.id, row.id),
          row.isCashierLoss || !canEdit,
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: ({ row }) => {
        const actions = getIconActions({
          seePicture: () => {
            setShouldPictureFullScreen({
              state: true,
              img: row.lossPictureUrl || get(row, 'lossPictureImage.data', null),
            });
          },
          replacePicture: () => onReplacePicture(row.id),
          deletePicture: () => handleDeletePicture(row.id),
        });

        return (
          <PictureWithActions
            actions={actions}
            disabled={row.isCashierLoss || !canEdit}
            image={row.lossPictureUrl || get(row, 'lossPictureImage.data', null)}
          />
        );
      },
    },
    {
      field: 'total',
      headerName: i18next.t('GENERAL.TOTAL_EX_TAX'),
      width: 120,
      sortable: true,
      renderCell: ({ row }) => {
        const costByChannelId = row.costByChannelId[row.channelId];

        const quantity = row.quantity * getFactorToConvertMetricToEntityUnit(row.unit);

        const total =
          costByChannelId && hasMultipleChannels ? costByChannelId * quantity : row.cost * quantity;

        return <DisplayNumber currency={currency} number={total} displayCurrencyCode />;
      },
    },
    {
      field: 'close',
      headerName: '',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.isCashierLoss || !canEdit) {
          return null;
        }

        return removeItemCell(row, handleItemRemoval);
      },
    },
  ];

  if (isCentralMode) {
    columns.splice(3, 0, {
      field: 'cost',
      headerName: i18next.t('GENERAL.COST_EX_TAX'),
      width: 100,
      sortable: true,
      renderCell: ({ row }) => {
        const costByChannelId = row.costByChannelId[row.channelId];

        const cost = costByChannelId || row.cost;

        return <DisplayNumber currency={currency} number={cost} displayCurrencyCode />;
      },
    });
  }

  if (hasMultipleChannels && !isCentralMode) {
    columns.splice(3, 0, {
      field: 'channel',
      headerName: i18next.t('GENERAL.CHANNEL'),
      width: 180,
      sortable: true,
      renderCell: ({ row }) => {
        const selectedChannel = row.channels.find(({ id }) => id === row.channelId);

        const recipeRowGroupedByRecipeids = groupBy(recipeRows, 'recipeId');

        const associatedLosses = recipeRowGroupedByRecipeids[row.recipeId] || [];

        const allAssociatedLossesCompositeKeys = associatedLosses.reduce((acc, { key }) => {
          if (!!key) {
            acc.push(key);
          }

          return acc;
        }, []);

        const formattedChannels = row.channels.map((channel) => {
          const compositeKeysForChannel = allAssociatedLossesCompositeKeys.filter((key) =>
            key.includes(channel.id),
          );

          if (compositeKeysForChannel.length === lossCategories.length) {
            return {
              ...channel,
              isDisabled: true,
            };
          }

          return channel;
        });

        const isDisabled = row.channels.length <= 1 || row.isCashierLoss || !canEdit;

        return renderDropdownCell(
          formattedChannels,
          selectedChannel,
          (item) => handleFieldUpdate('channelId', item.id, row.id),
          isDisabled,
        );
      },
    });
  }

  return columns;
};
